import { FacetEntity, NonRefinableFacetSubjectEntity } from '@cjm/shared/types';
import { FilterGroup, FilterItem, Filters, FiltersCount } from '@cjm/v-loket/shared';

/**
 * createFilterGroup
 *
 * The createFilterGroup function creates a FilterGroup object from a FacetEntity object.
 *
 * @param facetEntity{FacetEntity} - The FacetEntity object to convert to a FilterGroup object.
 * @returns {FilterGroup} - The FilterGroup object created from the FacetEntity object.
 */
export const createFilterGroup = (facetEntity: FacetEntity): FilterGroup => ({
	id: facetEntity?.id,
	name: facetEntity?.name,
	options: (Array.isArray(facetEntity?.items) ? facetEntity?.items : []).reduce(
		(options, item: NonRefinableFacetSubjectEntity) => {
			options[item.id] = { id: item.id, name: item.name };
			return options;
		},
		{} as Record<string, FilterItem>
	)
});

/**
 * createCountGroup
 *
 * The createCountGroup function creates a Record<string, number> object from a FacetEntity object.
 *
 * @param facetEntity{FacetEntity} - The FacetEntity object to convert to a Record<string, number> object.
 * @returns {Record<string, number>} - The Record<string, number> object created from the FacetEntity object.
 */
export const createCountGroup = (facetEntity: FacetEntity): Record<string, number> =>
	(Array.isArray(facetEntity?.items) ? facetEntity?.items : []).reduce(
		(countGroup, item: NonRefinableFacetSubjectEntity) => {
			countGroup[item.id] = item.amount;
			return countGroup;
		},
		{} as Record<string, number>
	);

/**
 * convertFacetEntities
 *
 * The convertFacetEntities function converts an array of FacetEntity objects to a Filters object and a FiltersCount object.
 * @param facetEntities{FacetEntity[]} - The array of FacetEntity objects to convert.
 * @returns {{filters: Filters; filterCount: FiltersCount}} - The Filters object and FiltersCount object created from the FacetEntity objects.
 */
export const convertFacetEntities = (
	facetEntities: FacetEntity[] = []
): { filters: Filters; filterCount: FiltersCount } =>
	facetEntities.reduce(
		(acc, facetEntity) => {
			acc.filters[facetEntity.id] = createFilterGroup(facetEntity);
			acc.filterCount[facetEntity.id] = createCountGroup(facetEntity);
			return acc;
		},
		{ filters: {}, filterCount: {} } as { filters: Filters; filterCount: FiltersCount }
	);
